
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { deletePlatformAdmin } from '@/services/platformAdminsService';
import { deleteOrganizationMember } from '@/services/organizationUsersService';
import { Loader2 } from 'lucide-react';

type User = {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Editor' | 'Viewer';
  status: 'Active' | 'Inactive';
  lastActive: string;
  invitationStatus?: 'pending' | 'accepted' | 'expired';
  invitedAt?: string;
  invitedBy?: string;
  user_type: 'platform_admin' | 'org_admin' | 'team_member';
  organization_id?: string;
  organization_name?: string;
};

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  onUserDeleted: () => void;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  onUserDeleted
}: DeleteUserDialogProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      // Delete from the appropriate table based on user type
      if (user.user_type === 'platform_admin') {
        await deletePlatformAdmin(user.id);
      } else {
        await deleteOrganizationMember(user.id);
      }
      
      toast({
        title: "Success",
        description: `User ${user.name} has been permanently deleted.`,
      });

      onUserDeleted();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      
      // Check if it's a foreign key constraint error
      if (error.message?.includes('foreign key') || error.message?.includes('referenced') || error.code === '23503') {
        toast({
          title: "Cannot Delete User",
          description: `Cannot delete ${user.name} because they have invited other users. Please reassign or remove the users they invited first.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete user. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to permanently delete <strong>{user.name}</strong> ({user.email})?
            <br />
            <br />
            This action cannot be undone. The user will be completely removed from the system.
            {user.user_type === 'platform_admin' && (
              <>
                <br />
                <br />
                <strong>Note:</strong> If this user has invited others, you'll need to reassign or remove those invitations first.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
